/**
 * CreatorRequestsPage.tsx — 1904va
 * The deal desk. Creator sees incoming license requests, approves with pricing,
 * license is granted, payout record created, buyer gets download.
 */
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { CheckCircle, XCircle, Clock, Download, Zap, ChevronDown, ChevronUp } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { Button, Spinner } from '@/components/ui'
import { cn } from '@/components/ui/utils'
import { useAuthStore } from '@/stores/authStore'
import { fetchPlatformConfig, calculatePlatformFee } from '@/lib/supabase'

type LicenseRequest = {
  id: string
  status: 'pending' | 'approved' | 'rejected'
  message: string | null
  requested_at: string
  requester_id: string
  media_items: {
    id: string
    cloudinary_url: string
    media_type: string
    file_name: string
    ai_tags: string[]
    ai_description: string
    ai_route_score: number
    ai_grade: string
    ai_editor_note: string
    participant_id: string
  }
  participants: {
    name: string
    handle: string | null
    role: string
  } | null
}

const USAGE_TYPES = [
  { value: 'editorial',    label: 'Editorial',     desc: 'News, press, non-commercial' },
  { value: 'commercial',   label: 'Commercial',    desc: 'Advertising, marketing' },
  { value: 'exclusive',    label: 'Exclusive',     desc: 'Exclusive rights, full buyout' },
  { value: 'social',       label: 'Social media',  desc: 'Digital and social only' },
]

function RequestCard({ req, participantId: _participantId, onDecide }: {
  req: LicenseRequest
  participantId: string
  onDecide: (id: string, decision: 'approved' | 'rejected', price: number, usageType: string) => void
}) {
  const [expanded,  setExpanded]  = useState(true)
  const [price,     setPrice]     = useState('')
  const [usageType, setUsageType] = useState('editorial')
  const [deciding,  setDeciding]  = useState(false)

  const media = req.media_items
  const grade = media.ai_grade
  const score = media.ai_route_score

  async function handleDecide(decision: 'approved' | 'rejected') {
    setDeciding(true)
    await onDecide(req.id, decision, parseFloat(price) || 0, usageType)
    setDeciding(false)
  }

  return (
    <div className={cn(
      'rounded-2xl border overflow-hidden transition-all',
      req.status === 'pending'  ? 'border-room-border bg-room-surface' :
      req.status === 'approved' ? 'border-emerald-800/40 bg-emerald-950/10' :
      'border-neutral-800 bg-neutral-900/40 opacity-60'
    )}>
      {/* Image + header */}
      <div className="relative">
        {media.media_type === 'photo' && (
          <img
            src={media.cloudinary_url}
            alt=""
            className="w-full h-40 object-cover"
          />
        )}
        {/* Grade overlay */}
        {grade && (
          <div className="absolute top-2 left-2">
            <span className={cn(
              'text-xs font-black px-2 py-1 rounded-lg',
              grade === 'A' ? 'bg-emerald-900/90 text-emerald-300' :
              grade === 'B' ? 'bg-blue-900/90 text-blue-300' :
              grade === 'C' ? 'bg-amber-900/90 text-amber-300' :
              'bg-red-900/90 text-red-300'
            )}>
              {grade}
            </span>
          </div>
        )}
        {/* Score overlay */}
        <div className="absolute top-2 right-2 flex items-center gap-1 bg-black/70 rounded-lg px-2 py-1">
          <Zap size={10} className="text-room-gold" />
          <span className="text-xs text-room-gold font-bold">{Math.round((score ?? 0) * 100)}%</span>
        </div>
        {/* Status badge for decided */}
        {req.status !== 'pending' && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50">
            {req.status === 'approved'
              ? <CheckCircle size={40} className="text-emerald-400" />
              : <XCircle size={40} className="text-red-400" />
            }
          </div>
        )}
      </div>

      <div className="p-4 space-y-3">
        {/* Requester */}
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-white font-semibold text-sm">
              {req.participants?.name ?? 'Unknown'}
              {req.participants?.role && (
                <span className="ml-2 text-xs text-neutral-500 capitalize">{req.participants.role}</span>
              )}
            </p>
            <p className="text-neutral-500 text-xs mt-0.5">{media.file_name}</p>
          </div>
          <button
            onClick={() => setExpanded(v => !v)}
            className="text-neutral-500 hover:text-white transition-colors"
          >
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>

        {/* AI editor note */}
        {media.ai_editor_note && (
          <p className="text-xs text-neutral-400 italic border-l-2 border-room-accent/30 pl-2 leading-relaxed">
            {media.ai_editor_note}
          </p>
        )}

        {/* Request message */}
        {req.message && (
          <div className="bg-neutral-800/50 rounded-xl p-3">
            <p className="text-xs text-neutral-300 italic">"{req.message}"</p>
          </div>
        )}

        {/* Deal desk — only for pending */}
        {req.status === 'pending' && expanded && (
          <div className="space-y-3 pt-2 border-t border-room-border">
            {/* Usage type */}
            <div className="grid grid-cols-2 gap-1.5">
              {USAGE_TYPES.map(u => (
                <button
                  key={u.value}
                  onClick={() => setUsageType(u.value)}
                  className={cn(
                    'px-3 py-2 rounded-xl text-left transition-all',
                    usageType === u.value
                      ? 'bg-room-accent/20 border border-room-accent text-white'
                      : 'bg-neutral-800 border border-transparent text-neutral-400 hover:text-white'
                  )}
                >
                  <p className="text-xs font-semibold">{u.label}</p>
                  <p className="text-[10px] text-neutral-500 mt-0.5">{u.desc}</p>
                </button>
              ))}
            </div>

            {/* Price */}
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400 text-sm font-bold">£</span>
              <input
                type="number"
                value={price}
                onChange={e => setPrice(e.target.value)}
                placeholder="0 — set your price"
                className="w-full bg-black/40 border border-room-border rounded-xl pl-8 pr-4 py-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-room-accent focus:border-transparent transition-all placeholder-neutral-600"
              />
            </div>

            {/* AI suggested price hint */}
            {grade === 'A' && (
              <p className="text-xs text-room-gold">
                Grade A content — suggested range £150–£400 editorial, £500+ exclusive
              </p>
            )}
            {grade === 'B' && (
              <p className="text-xs text-neutral-500">
                Grade B content — suggested range £75–£150 editorial
              </p>
            )}

            {/* Actions */}
            <div className="flex gap-2">
              <Button
                onClick={() => handleDecide('approved')}
                disabled={deciding}
                className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-3"
              >
                {deciding ? <Spinner size={16} /> : 'Approve & license'}
              </Button>
              <Button
                onClick={() => handleDecide('rejected')}
                disabled={deciding}
                variant="ghost"
                className="border border-red-800/50 text-red-400 hover:bg-red-900/20 px-4"
              >
                Decline
              </Button>
            </div>
          </div>
        )}

        {/* Approved state */}
        {req.status === 'approved' && (
          <div className="flex items-center gap-2 text-emerald-400 text-sm">
            <CheckCircle size={14} />
            <span>Licensed — buyer has download access</span>
          </div>
        )}
      </div>
    </div>
  )
}

export default function CreatorRequestsPage() {
  const { participant } = useAuthStore()
  const qc = useQueryClient()

  const { data: requests = [], isLoading } = useQuery({
    queryKey: ['license-requests', participant?.id],
    queryFn: async () => {
      if (!participant?.id) return []
      const { data, error } = await supabase
        .from('license_requests')
        .select(`
          id, status, message, requested_at, requester_id,
          media_items!inner(
            id, cloudinary_url, media_type, file_name,
            ai_tags, ai_description, ai_route_score,
            ai_grade, ai_editor_note, participant_id
          ),
          participants(name, handle, role)
        `)
        .eq('media_items.participant_id', participant.id)
        .order('requested_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as unknown as LicenseRequest[]
    },
    enabled: !!participant?.id,
    refetchInterval: 15_000,
  })

  const { data: platformConfig } = useQuery({
    queryKey: ['platform-config'],
    queryFn:  fetchPlatformConfig,
    staleTime: 10 * 60_000,  // config changes rarely
  })

  const decideMutation = useMutation({
    mutationFn: async ({
      id, decision, price, usageType
    }: { id: string; decision: 'approved' | 'rejected'; price: number; usageType: string }) => {
      const req = requests.find(r => r.id === id)
      if (!req) throw new Error('Request not found')

      // 1. Update request status
      const { error: updateErr } = await supabase
        .from('license_requests')
        .update({ status: decision, decided_at: new Date().toISOString() })
        .eq('id', id)
      if (updateErr) throw updateErr

      if (decision === 'approved') {
        // 2. Create license record
        const platformFee = platformConfig
          ? calculatePlatformFee(price, platformConfig)
          : 0  // safe default during evaluation
        const { data: license, error: licErr } = await supabase
          .from('licenses')
          .insert({
            media_item_id: req.media_items.id,
            licensee_id:   req.requester_id,
            licensor_id:   participant?.id,
            usage_type:    usageType,
            download_url:  req.media_items.cloudinary_url,
          })
          .select('id')
          .single()
        if (licErr) throw licErr

        // 3. Create payout record if price > 0
        if (price > 0 && license) {
          await supabase.from('payouts').insert({
            license_id:   license.id,
            creator_id:   participant?.id,
            amount:       price - platformFee,
            currency:     'GBP',
            platform_fee: platformFee,
            status:       'pending',
          })
        }
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['license-requests'] })
    },
  })

  const pending  = requests.filter(r => r.status === 'pending')
  const approved = requests.filter(r => r.status === 'approved')
  const rejected = requests.filter(r => r.status === 'rejected')

  if (!participant) {
    return <div className="flex items-center justify-center h-64 text-neutral-500">Sign in to see requests</div>
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">License requests</h1>
          <p className="text-neutral-400 text-sm mt-0.5">Approve requests and set your price</p>
        </div>
        {pending.length > 0 && (
          <span className="px-3 py-1 rounded-full bg-room-accent/20 border border-room-accent/40 text-room-accent text-sm font-bold">
            {pending.length} pending
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : requests.length === 0 ? (
        <div className="text-center py-16 text-neutral-500">
          <Download size={32} className="mx-auto mb-4 opacity-30" />
          <p className="font-medium">No requests yet</p>
          <p className="text-sm mt-1">When buyers request your content, deals appear here</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Pending */}
          {pending.length > 0 && (
            <section className="space-y-3">
              <p className="text-xs font-semibold text-neutral-400 uppercase tracking-widest flex items-center gap-2">
                <Clock size={12} /> Awaiting decision
              </p>
              {pending.map(req => (
                <RequestCard
                  key={req.id}
                  req={req}
                  participantId={participant.id}
                  onDecide={(id, decision, price, usageType) =>
                    decideMutation.mutateAsync({ id, decision, price, usageType })
                  }
                />
              ))}
            </section>
          )}

          {/* Approved */}
          {approved.length > 0 && (
            <section className="space-y-3">
              <p className="text-xs font-semibold text-neutral-400 uppercase tracking-widest flex items-center gap-2">
                <CheckCircle size={12} className="text-emerald-400" /> Licensed
              </p>
              {approved.map(req => (
                <RequestCard
                  key={req.id}
                  req={req}
                  participantId={participant.id}
                  onDecide={(id, decision, price, usageType) =>
                    decideMutation.mutateAsync({ id, decision, price, usageType })
                  }
                />
              ))}
            </section>
          )}

          {/* Rejected */}
          {rejected.length > 0 && (
            <section className="space-y-3">
              <p className="text-xs font-semibold text-neutral-400 uppercase tracking-widest">Declined</p>
              {rejected.map(req => (
                <RequestCard
                  key={req.id}
                  req={req}
                  participantId={participant.id}
                  onDecide={(id, decision, price, usageType) =>
                    decideMutation.mutateAsync({ id, decision, price, usageType })
                  }
                />
              ))}
            </section>
          )}
        </div>
      )}
    </div>
  )
}
